import { BillingDefaultsPreview } from '../components/billing/BillingWorkflowTools'
import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Textarea,
  Divider,
  Button,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/joy'
import { FloppyDisk as Save, Gear as Settings } from '@phosphor-icons/react'
import api from '../lib/api'
import { documentTemplatesApi } from '../lib/billingApi'
import { PAYMENT_TERMS, normalizePaymentTerms } from '../components/billing/paymentTerms'
import type { DocumentTemplate } from '../types/billing'

const DOCUMENT_EMAIL_SECTIONS = [
  { key: 'estimate', label: 'Estimate', placeholder: 'Your estimate from {company_name}' },
  { key: 'invoice', label: 'Invoice', placeholder: 'Invoice {document_number} from {company_name}' },
  { key: 'receipt', label: 'Payment receipt', placeholder: 'Payment receipt from {company_name}' },
] as const

// The boilerplate every new document starts from. It is stored per document
// type on the org's default DocumentTemplate — the same record the PDF renderer
// already falls back to for terms.
const DOCUMENT_DEFAULT_SECTIONS = [
  { type: 'estimate', label: 'Estimates', templateName: 'Default Estimate' },
  { type: 'invoice', label: 'Invoices', templateName: 'Default Invoice' },
] as const

type DocumentDefaults = {
  id: number | null
  terms_and_conditions: string
  default_payment_terms: string
  default_notes: string
}

const emptyDefaults = (): DocumentDefaults => ({
  id: null,
  terms_and_conditions: '',
  default_payment_terms: 'net_30',
  default_notes: '',
})

const defaultsFromTemplate = (template: DocumentTemplate): DocumentDefaults => ({
  id: template.id,
  terms_and_conditions: template.terms_and_conditions || '',
  default_payment_terms: normalizePaymentTerms(template.default_payment_terms),
  default_notes: template.default_notes || '',
})

interface OrganizationData {
  average_cost_per_square?: number | null
  target_gross_per_square?: number | null
  target_overhead?: number | null
  target_profit?: number | null
  default_tax_rate?: number | null
  payment_url?: string
  estimate_email_subject?: string
  estimate_email_body?: string
  invoice_email_subject?: string
  invoice_email_body?: string
  receipt_email_subject?: string
  receipt_email_body?: string
}

export default function BillingSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [formData, setFormData] = useState<OrganizationData>({
    average_cost_per_square: null,
    target_gross_per_square: null,
    target_overhead: null,
    target_profit: null,
    default_tax_rate: null,
    payment_url: '',
    estimate_email_subject: '',
    estimate_email_body: '',
    invoice_email_subject: '',
    invoice_email_body: '',
    receipt_email_subject: '',
    receipt_email_body: '',
  })
  const [documentDefaults, setDocumentDefaults] = useState<Record<string, DocumentDefaults>>({
    estimate: emptyDefaults(),
    invoice: emptyDefaults(),
  })

  // Fetch current organization settings and tax rates
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get<{
          average_cost_per_square?: number | null
          target_gross_per_square?: number | null
          target_overhead?: number | null
          target_profit?: number | null
          default_tax_rate?: number | null
          payment_url?: string
          estimate_email_subject?: string
          estimate_email_body?: string
          invoice_email_subject?: string
          invoice_email_body?: string
          receipt_email_subject?: string
          receipt_email_body?: string
        }>('/api/organizations/current/')
        setFormData({
          average_cost_per_square: response.data.average_cost_per_square || null,
          target_gross_per_square: response.data.target_gross_per_square || null,
          target_overhead: response.data.target_overhead || null,
          target_profit: response.data.target_profit || null,
          default_tax_rate: response.data.default_tax_rate || null,
          payment_url: response.data.payment_url || '',
          estimate_email_subject: response.data.estimate_email_subject || '',
          estimate_email_body: response.data.estimate_email_body || '',
          invoice_email_subject: response.data.invoice_email_subject || '',
          invoice_email_body: response.data.invoice_email_body || '',
          receipt_email_subject: response.data.receipt_email_subject || '',
          receipt_email_body: response.data.receipt_email_body || '',
        })
      } catch (err: any) {
        setError('Failed to load billing settings')
        console.error('Error fetching organization:', err)
      } finally {
        setLoading(false)
      }
    }

    const fetchDocumentDefaults = async () => {
      try {
        const responses = await Promise.all(
          DOCUMENT_DEFAULT_SECTIONS.map(section => documentTemplatesApi.list(section.type))
        )
        setDocumentDefaults(prev => {
          const next = { ...prev }
          DOCUMENT_DEFAULT_SECTIONS.forEach((section, index) => {
            const template = (responses[index].results || []).find(t => t.is_default && t.is_active)
            if (template) next[section.type] = defaultsFromTemplate(template)
          })
          return next
        })
      } catch (err: any) {
        console.error('Error fetching document defaults:', err)
      }
    }

    fetchSettings()
    fetchDocumentDefaults()
  }, [])

  const handleInputChange = (field: keyof OrganizationData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? null : parseFloat(value),
    }))
    setSuccess(false) // Reset success message when user types
  }

  const handleTextChange =
    (field: keyof OrganizationData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    }

  const setDocumentDefault = (
    documentType: string,
    field: keyof Omit<DocumentDefaults, 'id'>,
    value: string
  ) => {
    setDocumentDefaults(prev => ({
      ...prev,
      [documentType]: { ...prev[documentType], [field]: value },
    }))
    setSuccess(false)
  }

  // One default template per document type. It is created on first save rather
  // than up front, so an org that never sets defaults grows no stray records.
  const saveDocumentDefaults = async () => {
    await Promise.all(
      DOCUMENT_DEFAULT_SECTIONS.map(async section => {
        const values = documentDefaults[section.type]
        const payload = {
          terms_and_conditions: values.terms_and_conditions,
          default_payment_terms: values.default_payment_terms,
          default_notes: values.default_notes,
        }
        if (values.id) {
          await documentTemplatesApi.update(values.id, payload)
          return
        }
        if (!payload.terms_and_conditions && !payload.default_notes && payload.default_payment_terms === 'net_30') {
          return
        }
        const created = await documentTemplatesApi.create({
          ...payload,
          name: section.templateName,
          template_type: section.type,
          is_default: true,
          is_active: true,
          // The PDF renderer reads this off the template once one exists, and
          // its model default is off — which would silently drop the pay-online
          // QR that documents show today.
          enable_qr_code: true,
        })
        setDocumentDefaults(prev => ({ ...prev, [section.type]: defaultsFromTemplate(created) }))
      })
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(false)

    try {
      await api.patch('/api/organizations/current/', formData)
      await saveDocumentDefaults()
      setSuccess(true)
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError('Failed to save billing settings')
      console.error('Error saving settings:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Settings />
          Billing Settings
        </Typography>
        <Typography level="body-md" color="neutral">
          Configure default values for estimates and invoices
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert color="success" sx={{ mb: 3 }}>
          Billing settings saved successfully!
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Settings Form */}
      <Card>
        <Stack spacing={3}>
          <Typography level="title-lg">Default Pricing</Typography>
          
          <FormControl>
            <FormLabel>Average Cost Per Square</FormLabel>
            <Input
              type="number"
              placeholder="Enter amount"
              value={formData.average_cost_per_square || ''}
              onChange={handleInputChange('average_cost_per_square')}
              startDecorator="$"
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              The average cost per square foot for materials and labor
            </Typography>
          </FormControl>

          <FormControl>
            <FormLabel>Target Gross Per Square</FormLabel>
            <Input
              type="number"
              placeholder="Enter amount"
              value={formData.target_gross_per_square || ''}
              onChange={handleInputChange('target_gross_per_square')}
              startDecorator="$"
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              The target gross profit per square foot
            </Typography>
          </FormControl>

          <FormControl>
            <FormLabel>Target Overhead</FormLabel>
            <Input
              type="number"
              placeholder="Enter percentage"
              value={formData.target_overhead || ''}
              onChange={handleInputChange('target_overhead')}
              endDecorator="%"
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                  max: 100,
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              The target overhead percentage (default: 10%)
            </Typography>
          </FormControl>

          <FormControl>
            <FormLabel>Target Profit</FormLabel>
            <Input
              type="number"
              placeholder="Enter percentage"
              value={formData.target_profit || ''}
              onChange={handleInputChange('target_profit')}
              endDecorator="%"
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                  max: 100,
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              The target profit margin percentage (default: 10%)
            </Typography>
          </FormControl>

          <FormControl>
            <FormLabel>Default Tax Rate</FormLabel>
            <Input
              type="number"
              placeholder="Enter tax rate"
              value={formData.default_tax_rate || ''}
              onChange={handleInputChange('default_tax_rate')}
              endDecorator="%"
              slotProps={{
                input: {
                  step: 0.01,
                  min: 0,
                  max: 100,
                },
              }}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              Automatically applied to new line items on estimates and invoices
            </Typography>
          </FormControl>

          <Divider />

          <Box>
            <Typography level="title-lg">Document Defaults</Typography>
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              What a new estimate or invoice starts with. Every field stays editable on the
              document itself, and editing it there never changes these.
            </Typography>
          </Box>

          {DOCUMENT_DEFAULT_SECTIONS.map(({ type, label }) => (
            <Stack key={type} spacing={1.5}>
              <Typography level="title-sm">{label}</Typography>
              <FormControl>
                <FormLabel>Payment Terms</FormLabel>
                <Select
                  value={documentDefaults[type].default_payment_terms}
                  onChange={(_, value) => value && setDocumentDefault(type, 'default_payment_terms', value)}
                >
                  {PAYMENT_TERMS.map(term => (
                    <Option key={term.value} value={term.value}>
                      {term.label}
                    </Option>
                  ))}
                </Select>
                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                  The due date is worked out from these terms unless you set one by hand.
                </Typography>
              </FormControl>
              <FormControl>
                <FormLabel>Terms &amp; Conditions</FormLabel>
                <Textarea
                  minRows={4}
                  placeholder="Warranty, payment and scope terms printed on the document"
                  value={documentDefaults[type].terms_and_conditions}
                  onChange={(e) => setDocumentDefault(type, 'terms_and_conditions', e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Notes (visible to customer)</FormLabel>
                <Textarea
                  minRows={2}
                  placeholder="Leave blank for none"
                  value={documentDefaults[type].default_notes}
                  onChange={(e) => setDocumentDefault(type, 'default_notes', e.target.value)}
                />
              </FormControl>
            </Stack>
          ))}

          <Divider />

          <Box>
            <Typography level="title-lg">Customer Payments</Typography>
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              Where customers can pay you online. This is the same setting as
              Settings &rarr; Organization; changing it in either place updates both.
            </Typography>
          </Box>

          <FormControl>
            <FormLabel>Pay Online URL</FormLabel>
            <Input
              type="url"
              placeholder="https://pay.example.com"
              value={formData.payment_url || ''}
              onChange={handleTextChange('payment_url')}
            />
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              A Stripe payment link, QuickBooks portal, or similar. Invoice views and default invoice emails include it. PDFs show a clickable
              "Pay online" line next to a scannable QR code. Leave blank to hide both.
            </Typography>
          </FormControl>

          <Divider />

          <Box>
            <Typography level="title-lg">Document Emails</Typography>
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              The default subject and message used when you email an estimate, invoice, or payment
              receipt. You can still edit any of it right before sending. Leave a field blank to use
              the built-in wording.
            </Typography>
            <Typography level="body-sm" sx={{ mt: 1 }}>
              Available placeholders: <code>{'{customer_name}'}</code>{' '}
              <code>{'{customer_first_name}'}</code> <code>{'{document_number}'}</code>{' '}
              <code>{'{document_total}'}</code> <code>{'{balance_due}'}</code>{' '}
              <code>{'{company_name}'}</code> <code>{'{company_phone}'}</code>{' '}<code>{'{payment_url}'}</code>
            </Typography>
          </Box>

          {DOCUMENT_EMAIL_SECTIONS.map(({ key, label, placeholder }) => (
            <Stack key={key} spacing={1.5}>
              <Typography level="title-sm">{label}</Typography>
              <FormControl>
                <FormLabel>Subject</FormLabel>
                <Input
                  placeholder={placeholder}
                  value={formData[`${key}_email_subject` as const] || ''}
                  onChange={handleTextChange(`${key}_email_subject` as const)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  minRows={5}
                  placeholder="Leave blank to use the built-in wording"
                  value={formData[`${key}_email_body` as const] || ''}
                  onChange={handleTextChange(`${key}_email_body` as const)}
                />
              </FormControl>
            </Stack>
          ))}

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
            <Button
              size="lg"
              startDecorator={<Save />}
              onClick={handleSave}
              loading={saving}
              disabled={saving}
            >
              Save Settings
            </Button>
          </Box>
        </Stack>
      </Card>
      <BillingDefaultsPreview values={formData} defaults={documentDefaults}/>
    </Box>
  )
}